import h from 'hyperscript';

import privateStateIcon from '@disy/cadenza-icons/private-state.svg';
import { openUrl } from '@disy/lib/open-url';
import { createIconButton } from '@disy/ui/button';
import { escapeAndMarkHTML } from '@disy/ui/escape-and-mark';
import { icon } from '@disy/ui/icon';
import { setupMenuButton } from '@disy/ui/menu';

import 'ui/tag/tag.css';

import { createPublicWorkbookUrl } from 'cadenza/api-client/workbook-management-api';
import type { PublicationStage } from 'cadenza/repository/model/publication-stage.gen';
import type { WorkbookRelation } from 'cadenza/workbook/api/workbook-relation';

import i18n from './version-tag-provider.properties';

export type SavedState = 'unsaved' | 'saved';
type VersionMode = PublicationStage | SavedState;

interface VersionTagOptions {
  disabled?: boolean;
  onclick?: EventListenerOrEventListenerObject;
  modified?: boolean;
  short?: boolean;
  tagName?: keyof HTMLElementTagNameMap;
}

interface VersionTagOptionsShort extends VersionTagOptions {
  short: true;
}

interface VersionTagOptionsLong extends VersionTagOptions {
  short?: false;
  version: number;
}

export function createVersionTag(versionMode: VersionMode, options: VersionTagOptionsLong | VersionTagOptionsShort) {
  const { short, disabled, modified = false, onclick } = options;
  const tagClass = getClass(versionMode);
  const text = short ? createShortText(modified, versionMode) : createFullText(modified, versionMode, options.version);
  return h(
    `${options.tagName ?? 'button.hidden-xs'}.d-tag${tagClass ? '.' + tagClass : ''}`, // Uses native button element
    { disabled, onclick },
    h('span.ellipsis', text),
  );
}

// maps the version mode to the corresponding class
function getClass(versionMode: VersionMode) {
  switch (versionMode) {
    case 'PUBLISHED':
      return 'success';
    case 'DEVELOPMENT':
      return 'info';
    default:
      return undefined;
  }
}

function createShortText(modified: boolean, versionMode: VersionMode) {
  return i18n(`label.<${modified ? 'modified' : versionMode}>`);
}

function createFullText(modified: boolean, versionMode: VersionMode, version: number) {
  return modified ?
      i18n(`label.full.<${version ? 'modified' : 'unsaved'}>`, { version })
    : i18n(`label.full.<${versionMode}>`, { version });
}

export function createSnapshotTag(relation?: WorkbookRelation) {
  if (relation) {
    const button = createIconButton(icon(privateStateIcon), i18n('snapshot-tag.button.title'), {
      variant: 'borderless',
      styleClass: ['hidden-xs', 'button-s'],
    });
    setupMenuButton(
      button,
      () => [
        h('d-alert.d-alert-info.d-alert-small', {
          innerHTML: escapeAndMarkHTML(
            i18n('snapshot-tag.dialog.header', {
              version: relation.parentVersion,
              parent: relation.parentPrintName,
            }),
          ),
        }),
        {
          label: i18n('snapshot-tag.button.title'),
          execute: () => openUrl(createPublicWorkbookUrl(relation.parent, relation.worksheetId).toString()),
        },
      ],
      { styleClass: 'worksheet-navigation--menu' },
    );
    return button;
  }
}
